import { useCallback, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Creators as GraphqlCreators } from "model/graphql"
import useResource from "hooks/resources"
import { ReduxState } from "model/state"
import type { DataStatus } from "helpers/data-statuses"

interface ExperimentMetadata {
  id: string
  name: string
  description?: string
  availableFrom?: string
  type?: string
  lastUpdated?: string
}

export function usePortfolioExperiments(portfolioId: string | undefined): {
  experiments: ExperimentMetadata[]
  status: DataStatus
  refetch: () => void
} {
  const definition = useMemo(() => ({
    resource: "portfolioExperiments",
    url: () => {
      if (!portfolioId) return null
      return {
        id: `portfolioExperiments-${portfolioId}`,
        query: `query ($portfolioId: String!) {
          portfolio(id: $portfolioId) {
            experiments { id, name, description, availableFrom, type, lastUpdated }
          }
        }`,
        variables: { portfolioId },
      }
    },
  }), [portfolioId])

  const dispatch = useDispatch()
  const account = useSelector((state: ReduxState) => state.global.selected.account)

  const refetch = useCallback(() => {
    if (!portfolioId) return
    const payload = definition.url()
    if (!payload) return
    dispatch(GraphqlCreators.load(
      "portfolioExperiments",
      payload.id,
      payload.query,
      payload.variables,
      account,
      // Dedupe key so refetch is not skipped while another load with the same canonical id is in flight.
      { loadInstanceKey: String(Date.now()) },
    ))
  }, [portfolioId, definition, dispatch, account])

  const [experiments, dataStatus] = useResource<ExperimentMetadata[]>(
    definition,
    ["portfolio", "experiments"],
  )

  const experimentList = experiments ?? []

  return {
    experiments: experimentList,
    status: dataStatus,
    refetch,
  }
}
